// React imports
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Third-party library imports
import { Card, Col, Container, Row } from "reactstrap";

// Redux imports
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import { setSelectedFinancialYear } from "../../../../ReduxToolkit/Slice/FinancialYearSlice";
import {
  closeModal,
  openModal,
} from "../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";

// Component imports
import Loader from "../../../../Layout/Loader/Loader";
import { H2 } from "../../../../AbstractElements";
import FinancialYearCycle from "../../../../CommonElements/YearPickerCycle";
import ConveyanceTable from "./table/ConveyanceTable";
import ConveyanceCards from "./cards/ConveyanceCards";
import ConveyanceFilterBar from "./filter/ConveyanceFilterBar";
import ConveyanceListModal from "./modal/ConveyanceListModal";
import ConveyanceAttachmentCollectionModal from "./modal/ConveyanceAttachmentModal";

// Utility imports
import {
  Conveyance,
  ConveyanceAttachementModal,
  conveyanceDeleteDialog,
  ConveyanceDetailsModal,
  conveyanceManagementPath,
} from "../../../../utils/Constant";
import { findAndCategorizedAttachment } from "../../../../utils/fileUtils";
import { dateConversion, formattedYMDDate } from "../../../../utils/dateUtils";

// Type imports
import { Nullable } from "../../../../Types/Common";
import { ConveyanceDetail } from "../../types";
import { ExtensionTypes } from "../../../reimbursement/types";
import {
  useDeleteConveyanceMutation,
  useListConveyanceQuery,
} from "../../../../ReduxToolkit/api/conveyance";
import { logErrors } from "../../../../utils/logErrors";
import { showApiResultAlert } from "../../../../utils/AlertManager/showApiAlert";
import "../../../attendance/attendance.scss";
import { useEmployeeProfile } from "../../../../hooks/useEmployeeProfile";
import { selectEmployeeSecondaryTabActive } from "../../../employeeManagement/redux/selector/EmployeeManagementSelector";
import { IEmployeeModulesConfig } from "../../../../Types/UserProfileDetails";
import AddConveyanceManagement from "../../addConvenyance/components/AddConveyanceManagement";
import { useModalSelector } from "../../../../hooks/useModalSelector";
import { getConfigEligibleDateRange } from "../../../../utils/getConfigEligibleDate";
import { getLocalStorage } from "../../../../utils/localStorageHelpers";

const ConfirmDeleteDialog = lazy(
  () => import("../../../../CommonElements/ConfirmDeleteDialog"),
);

const getInitialDates = () => {
  const today = new Date();
  return {
    start: new Date(today.getFullYear(), today.getMonth(), 1),
    end: new Date(today.getFullYear(), today.getMonth() + 1, 0),
  };
};

const ViewConveyance = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { start, end } = getInitialDates();

  // State Variables
  const [startDate, setStartDate] = useState<Date>(start);
  const [endDate, setEndDate] = useState<Date>(end);
  const [conveyanceDetails, setConveyanceDetails] =
    useState<Nullable<ConveyanceDetail>>(null);
  const [attachmentCollection, setAttachmentCollection] =
    useState<ExtensionTypes>({
      img: [],
      pdf: [],
      excel: [],
    });

  const [pageNo, setPageNo] = useState<number>(1);
  const [paginationPerPage, setPaginationPerPage] = useState<number>(100);
  const [pageTotal, setPageTotal] = useState<number>(0);
  const [searchParams, setSearchParams] = useState({
    startDate: formattedYMDDate(start),
    endDate: formattedYMDDate(end),
  });

  const [isAddConveyanceManagement, setIsAddConveyanceManagement] =
    useState<boolean>(false);

  const { employeeProfileDetails, loggedInEmployee, loggedInEmployeeRole } =
    useEmployeeProfile();
  const employeeId = Number(employeeProfileDetails?.jobInformation?.userId);

  // Modal Information from Redux
  const { isOpen, modalType, selectedId } = useModalSelector();

  const secondaryTabActive = useAppSelector(selectEmployeeSecondaryTabActive);

  const selectedFinancialYear = useAppSelector(
    ({ financialYearSlice }) =>
      financialYearSlice.conveyance.selectedFinancialYear,
  );

  const isEmployeeManagementPage = location?.pathname.includes(
    "/user-management/user",
  );

  const financialYear = selectedFinancialYear?.split("-")[0];

  const shouldFetch =
    (location.pathname === "/employee-zone/conveyance" &&
      financialYear &&
      employeeId) ||
    (employeeId && Number(secondaryTabActive) === 301 && financialYear);

  const {
    data: conveyanceList,
    isLoading,
    isFetching,
    isSuccess,
    isError: isConveyanceListError,
    error: conveyanceListError,
  } = useListConveyanceQuery(
    {
      user: employeeId.toString(),
      startDate: searchParams.startDate,
      endDate: searchParams.endDate,
      activeYear: Number(financialYear),
      page: pageNo,
      size: paginationPerPage,
    },
    { skip: !shouldFetch || !financialYear },
  );

  const [
    deleteConveyance,
    { isLoading: isDeleteConveyanceLoading, reset: resetDeleteConveyance },
  ] = useDeleteConveyanceMutation();

  const employeeModuleConfig = getLocalStorage<IEmployeeModulesConfig | null>(
    "userConfig",
    null,
  );
  const { isBypassedRole } = getConfigEligibleDateRange(
    employeeModuleConfig,
    loggedInEmployeeRole,
  );

  const isLoadingConveyanceList = isLoading || isFetching;

  const isSameEmployeeId =
    Number(employeeProfileDetails?.jobInformation?.userId) ===
    Number(loggedInEmployee?.id);

  const handleSearch = useCallback(() => {
    if (!startDate || !endDate) return;
    setSearchParams({
      startDate: formattedYMDDate(startDate),
      endDate: formattedYMDDate(endDate),
    });
  }, [startDate, endDate]);

  // Navigate to Add Conveyance
  const handleAddConveyance = () => {
    if (!location.pathname.includes("/employee-zone/add-conveyance")) {
      setIsAddConveyanceManagement(true);
    } else {
      navigate(conveyanceManagementPath, {
        state: { userId: employeeId },
      });
    }
  };

  // Handle Start Date Change
  const handleChangeStartDate = useCallback(
    (startDate: Date) => {
      setStartDate(startDate);
    },
    [endDate],
  );

  // Handle End Date Change
  const handleChangeEndDate = useCallback(
    (endDate: Date) => {
      setEndDate(endDate);
    },
    [startDate],
  );

  const findToBeDeleteConveyance = useMemo(() => {
    if (!selectedId) return logErrors("Delete conveyance id not found");
    return conveyanceList?.data?.conveyance?.data?.find(
      ({ id }) => Number(id) === Number(selectedId),
    );
  }, [selectedId, conveyanceList]);

  const handleRowPerPageChange = (newPerPage: number) => {
    setPaginationPerPage(newPerPage);
  };

  const handlePageChange = (newPageNo: number) => {
    setPageNo(newPageNo);
  };

  // Open Conveyance Detail Modal
  const handleOpenModal = (userConveyanceDetails: ConveyanceDetail) => {
    setConveyanceDetails(userConveyanceDetails);
    dispatch(openModal({ modalType: ConveyanceDetailsModal }));
  };

  // Open Attachment Modal
  const handleOpenAttachmentModal = (
    conveyanceUserDetails: ConveyanceDetail,
  ) => {
    if (conveyanceUserDetails && conveyanceUserDetails.attachment) {
      const attachementUsers = findAndCategorizedAttachment(
        conveyanceUserDetails?.attachment,
      );
      if (attachementUsers) {
        setAttachmentCollection(attachementUsers);
      }
    }
    dispatch(openModal({ modalType: ConveyanceAttachementModal }));
  };

  const handleSetSelectedYear = (year: string) => {
    dispatch(setSelectedFinancialYear({ module: "conveyance", year }));
  };

  const handleOpenDialog = (conveyanceId: number) => {
    dispatch(
      openModal({
        modalType: conveyanceDeleteDialog,
        selectedId: conveyanceId,
      }),
    );
  };

  const handleResetDeleteConveyance = () => {
    resetDeleteConveyance();
    dispatch(closeModal());
  };

  const handleDeleteConveyance = async () => {
    if (!selectedId) return logErrors("Delete conveyance id is missing");

    const deleteConveyanceResponse = await deleteConveyance({
      id: Number(selectedId),
    });

    const alertMeta = {
      title: `Hey ${employeeProfileDetails?.jobInformation?.name}!`,
      description: `Conveyance deleted successfully of ${dateConversion(
        findToBeDeleteConveyance?.date ?? "",
        false,
        true,
      )}`,
    };

    showApiResultAlert(
      deleteConveyanceResponse,
      alertMeta,
      handleResetDeleteConveyance,
      dispatch,
      true,
      true,
    );
  };

  useEffect(() => {
    if (conveyanceList?.success) {
      setPageTotal(conveyanceList?.data?.conveyance.total);
    }

    if (
      isSuccess &&
      conveyanceList?.success &&
      !conveyanceList?.data?.conveyance?.data.length
    ) {
      const apiResponse = {
        data: {
          success: false,
          message: `Looks like there are no conveyance entries between ${dateConversion(
            startDate ?? "",
            false,
            true,
          )} and ${dateConversion(endDate ?? "", false, true)}`,
        },
      };

      const conveyanceAlertMeta = {
        title: `Hey ${employeeProfileDetails?.jobInformation?.name}!`,
        source: "",
      };

      showApiResultAlert(apiResponse, conveyanceAlertMeta);
    }

    if (isConveyanceListError) {
      showApiResultAlert(conveyanceListError, {
        title: `Hey ${loggedInEmployee?.name}!`,
      });
    }
  }, [conveyanceList, isConveyanceListError]);

  return (
    <div>
      {!location.pathname.includes("/employee-zone/add-conveyance") &&
        !isAddConveyanceManagement && (
          <Container fluid>
            <Row>
              <Col>
                <Card
                  className={`my-${
                    isEmployeeManagementPage ? "0" : "4"
                  } px-4 pt-3 pb-4`}
                >
                  <div
                    className={`d-flex justify-content-${
                      isEmployeeManagementPage ? "end" : "between"
                    } align-items-center`}
                  >
                    {!isEmployeeManagementPage && (
                      <H2 className="f-16">{Conveyance}</H2>
                    )}
                    <FinancialYearCycle
                      selectedYear={selectedFinancialYear ?? ""}
                      setSelectedYear={handleSetSelectedYear}
                      startMonth={9} // October (0-based index)
                      startDateValue={1}
                      endMonth={8} // September (0-based index)
                      endDateValue={30}
                    />
                  </div>
                  <ConveyanceFilterBar
                    location={location}
                    byPassRole={isBypassedRole}
                    isSameLoginId={isSameEmployeeId}
                    submit="submit"
                    primary="primary"
                    SearchLabel="Search"
                    EndDateLabel="End Date"
                    StartDateLabel="Start Date"
                    AddConveyanceLabel="Add Conveyance"
                    isConveyanceListLoading={isLoadingConveyanceList}
                    endDate={endDate}
                    startDate={startDate}
                    handleSearch={handleSearch}
                    handleChangeEndDate={handleChangeEndDate}
                    handleAddConveyance={handleAddConveyance}
                    handleChangeStartDate={handleChangeStartDate}
                  />

                  {/* Conveyance details table */}
                  <Row className="d-none d-lg-block">
                    <ConveyanceTable
                      total={pageTotal}
                      conveyanceList={conveyanceList!}
                      conveyanceListLoading={isLoadingConveyanceList}
                      perPage={paginationPerPage}
                      rowPerPageChange={handleRowPerPageChange}
                      pageChange={handlePageChange}
                      openModal={handleOpenModal}
                      openDialog={handleOpenDialog}
                      openAttachmentModal={handleOpenAttachmentModal}
                    />
                  </Row>
                </Card>
              </Col>
            </Row>
            <Row className="d-block d-lg-none">
              <Col xs="12">
                <Row>
                  {conveyanceList?.data?.conveyance?.data?.map(
                    (conveyanceInfo) => (
                      <ConveyanceCards
                        conveyanceInfo={conveyanceInfo}
                        conveyanceListLoading={isLoading}
                        openDialog={handleOpenDialog}
                      />
                    ),
                  )}
                </Row>
              </Col>
            </Row>
          </Container>
        )}
      {!location.pathname.includes("/employee-zone/add-conveyance") &&
        !isAddConveyanceManagement && (
          <Suspense fallback={<Loader />}>
            {modalType === ConveyanceDetailsModal &&
              isOpen &&
              conveyanceDetails && (
                <ConveyanceListModal details={conveyanceDetails} />
              )}
          </Suspense>
        )}
      {!location.pathname.includes("/employee-zone/add-conveyance") &&
        !isAddConveyanceManagement && (
          <Suspense fallback={<Loader />}>
            {modalType === ConveyanceAttachementModal && isOpen && (
              <ConveyanceAttachmentCollectionModal
                isOpen={isOpen}
                attachmentCollection={attachmentCollection}
              />
            )}
          </Suspense>
        )}
      {!location.pathname.includes("/employee-zone/add-conveyance") &&
        !isAddConveyanceManagement && (
          <Suspense fallback={<Loader />}>
            {isOpen && modalType === conveyanceDeleteDialog && (
              <ConfirmDeleteDialog
                isOpen={isOpen}
                size="md"
                label={`Are you sure want to remove conveyance of ${dateConversion(
                  findToBeDeleteConveyance?.date ?? "",
                  false,
                  true,
                )} ?`}
                handleToggle={() => dispatch(closeModal())}
                handleClickFirstBtn={handleDeleteConveyance}
                loading={isDeleteConveyanceLoading}
                loadingText={`Your conveyance record for ${dateConversion(
                  findToBeDeleteConveyance?.date ?? "",
                  false,
                  true,
                )} is being deleted. Please wait.`}
              />
            )}
          </Suspense>
        )}
      {isAddConveyanceManagement && (
        <AddConveyanceManagement
          isAddConveyanceManagement={isAddConveyanceManagement}
          setIsAddConveyanceManagement={setIsAddConveyanceManagement}
        />
      )}
    </div>
  );
};

export default ViewConveyance;
